<template>
  <div class="cost-table-page partner-tab-section">
    <div class="refined-header">
      <div>
        <h2 class="refined-title">Kâr Hesabı</h2>
        <p class="refined-subtitle">
          Üst bardaki <strong>Ürün</strong> ve <strong>MarketPlace</strong> seçimine göre listelenir.
          Komisyon/Stopaj = <strong>MP fiyatı × oran %</strong>;
          Maliyet (Has) = formülün 1., 2. ve 5. çarpanı × puan çarpanı, Maliyet (₺) = Maliyet (Has) × Has Altın.
          Ürün Kârı = MP fiyatı − komisyon − stopaj − hizmet − kargo − maliyet (₺);
          Gerçekleşen Kâr (Gr) = Ürün Kârı / Maliyet (Has).
          Gerçekleşen kâr girilince ürünün döviz değeri ve MP fiyatı buna göre güncellenir.
        </p>
      </div>
      <div class="refined-header-actions">
        <button type="button" class="refined-primary-btn" :disabled="loading" @click="load">
          <v-icon small class="mr-1">refresh</v-icon>
          Yenile
        </button>
        <button
          type="button"
          class="refined-primary-btn refined-btn-success"
          :class="{ 'refined-btn-success-emphasis': selectedEditable.length > 1 }"
          :disabled="!selectedEditable.length || !inputsReady || loading"
          :title="!inputsReady ? 'Önce kargo/hizmet bedelini girip «Karları Hesapla»ya basın' : 'Seçilenlerin kârını güncelle'"
          @click="openDialogFromSelection"
        >
          Seçilenlerin Kârını Güncelle ({{ selectedEditable.length }})
        </button>
      </div>
    </div>

    <div class="refined-filters">
      <div class="refined-filter-item">
        <label class="refined-filter-label">Kategori</label>
        <select v-model.number="categoryId" class="refined-select" @change="onFilterChange">
          <option v-for="c in categorySelectItems" :key="'cat-' + c.id" :value="c.id">{{ c.name }}</option>
        </select>
      </div>
      <div class="refined-filter-item">
        <label class="refined-filter-label">Kargo Bedeli (₺)</label>
        <input v-model="kargoInput" type="text" class="refined-input refined-input-narrow" placeholder="Örn. 150" />
      </div>
      <div class="refined-filter-item">
        <label class="refined-filter-label">Hizmet Bedeli (₺)</label>
        <input v-model="hizmetInput" type="text" class="refined-input refined-input-narrow" placeholder="Örn. 50" />
      </div>
      <div class="refined-filter-item">
        <label class="refined-filter-label">Komisyon (%)</label>
        <input v-model="komisyonInput" type="text" class="refined-input refined-input-narrow" placeholder="Örn. 7,5" />
      </div>
      <div class="refined-filter-item">
        <label class="refined-filter-label">Stopaj (%)</label>
        <input v-model="stopajInput" type="text" class="refined-input refined-input-narrow" placeholder="Örn. 1" />
      </div>
      <div class="refined-filter-item refined-filter-submit">
        <label class="refined-filter-label">&nbsp;</label>
        <button type="button" class="refined-primary-btn" :disabled="loading" @click="calculate">Karları Hesapla</button>
      </div>
    </div>

    <div class="refined-table-wrap">
      <div class="refined-table-scroll">
        <table class="refined-table cost-refined-table">
          <thead>
            <tr>
              <th class="refined-th-check">
                <v-checkbox
                  :input-value="headerCheckboxAll"
                  :indeterminate="headerCheckboxIndeterminate"
                  primary
                  hide-details
                  class="mt-0 pt-0"
                  @click.stop.prevent="toggleAll"
                ></v-checkbox>
              </th>
              <th class="refined-th-sort" @click="changeSort('productName')">
                Ürün
                <v-icon v-if="sortBy === 'productName'" small class="refined-sort-icon">{{
                  sortDesc ? "arrow_downward" : "arrow_upward"
                }}</v-icon>
              </th>
              <th class="refined-th-sort refined-th-num" @click="changeSort('marketplacePrice')">
                MP Fiyatı (₺)
                <v-icon v-if="sortBy === 'marketplacePrice'" small class="refined-sort-icon">{{
                  sortDesc ? "arrow_downward" : "arrow_upward"
                }}</v-icon>
              </th>
              <th class="refined-th-sort refined-th-num" @click="changeSort('hasGram')">
                Has Gram
                <v-icon v-if="sortBy === 'hasGram'" small class="refined-sort-icon">{{
                  sortDesc ? "arrow_downward" : "arrow_upward"
                }}</v-icon>
              </th>
              <th class="refined-th-sort refined-th-num" @click="changeSort('costHas')">
                Maliyet (Has)
                <v-icon v-if="sortBy === 'costHas'" small class="refined-sort-icon">{{
                  sortDesc ? "arrow_downward" : "arrow_upward"
                }}</v-icon>
              </th>
              <th class="refined-th-sort refined-th-num" @click="changeSort('costTl')">
                Maliyet (₺)
                <v-icon v-if="sortBy === 'costTl'" small class="refined-sort-icon">{{
                  sortDesc ? "arrow_downward" : "arrow_upward"
                }}</v-icon>
              </th>
              <th class="refined-th-sort refined-th-num" @click="changeSort('currentDoviz')">
                Döviz Fiyatı
                <v-icon v-if="sortBy === 'currentDoviz'" small class="refined-sort-icon">{{
                  sortDesc ? "arrow_downward" : "arrow_upward"
                }}</v-icon>
              </th>
              <th class="refined-th-sort refined-th-num" @click="changeSort('komisyonTl')">
                Komisyon (₺)
                <v-icon v-if="sortBy === 'komisyonTl'" small class="refined-sort-icon">{{
                  sortDesc ? "arrow_downward" : "arrow_upward"
                }}</v-icon>
              </th>
              <th class="refined-th-sort refined-th-num" @click="changeSort('stopajTl')">
                Stopaj (₺)
                <v-icon v-if="sortBy === 'stopajTl'" small class="refined-sort-icon">{{
                  sortDesc ? "arrow_downward" : "arrow_upward"
                }}</v-icon>
              </th>
              <th class="refined-th-sort refined-th-num" @click="changeSort('urunKari')">
                Ürün Kârı (₺)
                <v-icon v-if="sortBy === 'urunKari'" small class="refined-sort-icon">{{
                  sortDesc ? "arrow_downward" : "arrow_upward"
                }}</v-icon>
              </th>
              <th class="refined-th-sort refined-th-num" @click="changeSort('gerceklesenKar')">
                Gerçekleşen Kâr (Gr)
                <v-icon v-if="sortBy === 'gerceklesenKar'" small class="refined-sort-icon">{{
                  sortDesc ? "arrow_downward" : "arrow_upward"
                }}</v-icon>
              </th>
              <th class="refined-th-actions">Düzenle</th>
            </tr>
          </thead>
          <tbody>
            <tr v-if="loading">
              <td colspan="12" class="refined-loading">
                <v-progress-circular indeterminate color="primary" size="32"></v-progress-circular>
                Yükleniyor...
              </td>
            </tr>
            <tr v-else-if="!paginatedItems.length">
              <td colspan="12" class="refined-empty">Kayıt yok</td>
            </tr>
            <tr
              v-for="item in paginatedItems"
              v-else
              :key="item.mpProductId"
              class="refined-row"
              :class="{ 'cost-row-selected': isRowSelected(item) }"
              @click="onRowClick(item)"
            >
              <td class="refined-td-check" @click.stop>
                <v-checkbox
                  :input-value="isRowSelected(item)"
                  :disabled="!item.canCompute"
                  primary
                  hide-details
                  class="mt-0 pt-0"
                  @click.stop.prevent="toggleRowSelection(item)"
                ></v-checkbox>
              </td>
              <td class="refined-cell-bold">{{ item.productName || "—" }}</td>
              <td class="refined-th-num">
                <span v-if="item.marketplacePrice != null" class="refined-cell-value">{{ formatNum(item.marketplacePrice) }}</span>
                <span v-else class="refined-muted">—</span>
              </td>
              <td class="refined-th-num">
                <span v-if="item.hasGram != null" class="refined-cell-value">{{ formatGram(item.hasGram) }}</span>
                <span v-else class="refined-muted" :title="item.formula || ''">—</span>
              </td>
              <td class="refined-th-num">
                <span v-if="item.costHas != null" class="refined-cell-value">{{ formatGram(item.costHas) }}</span>
                <span v-else class="refined-muted" :title="item.formula || ''">—</span>
              </td>
              <td class="refined-th-num">
                <span v-if="item.costTl != null" class="refined-cell-value">{{ formatNum(item.costTl) }}</span>
                <span v-else class="refined-muted" :title="goldPrice == null ? 'Has Altın fiyatı yüklenemedi' : ''">—</span>
              </td>
              <td class="refined-th-num">
                <span v-if="item.currentDoviz != null" class="refined-cell-value">{{ formatNum(item.currentDoviz) }}</span>
                <span v-else class="refined-muted" title="Aktif döviz satırı yok — kaydedince oluşturulur">—</span>
              </td>
              <td class="refined-th-num">
                <span v-if="item.komisyonTl != null" class="refined-cell-value">{{ formatNum(item.komisyonTl) }}</span>
                <span v-else class="refined-muted">—</span>
              </td>
              <td class="refined-th-num">
                <span v-if="item.stopajTl != null" class="refined-cell-value">{{ formatNum(item.stopajTl) }}</span>
                <span v-else class="refined-muted">—</span>
              </td>
              <td class="refined-th-num">
                <span v-if="item.urunKari != null" class="refined-cell-value">{{ formatNum(item.urunKari) }}</span>
                <span v-else class="refined-muted">—</span>
              </td>
              <td class="refined-th-num">
                <span v-if="item.gerceklesenKar != null" class="refined-cell-value refined-kar">{{ formatNum(item.gerceklesenKar) }}</span>
                <span v-else class="refined-muted">—</span>
              </td>
              <td class="refined-td-actions" @click.stop>
                <button
                  type="button"
                  class="refined-action-btn"
                  :class="{ 'refined-action-btn-muted': selectedEditable.length > 1 && item.canCompute }"
                  :disabled="!item.canCompute || !inputsReady || selectedEditable.length > 1"
                  :title="editTitle(item)"
                  @click="openDialogForRow(item)"
                >
                  Düzenle
                </button>
              </td>
            </tr>
          </tbody>
        </table>
      </div>
      <div class="refined-table-footer">
        <div class="refined-footer-left">
          <span>Sayfa başına satır:</span>
          <select v-model.number="rowsPerPage" class="refined-rows-select" @change="page = 1">
            <option :value="25">25</option>
            <option :value="50">50</option>
            <option :value="100">100</option>
            <option :value="200">200</option>
          </select>
        </div>
        <span class="refined-pagination-info">{{ paginationInfo }}</span>
        <div class="refined-footer-actions">
          <button type="button" class="refined-page-btn" :disabled="page <= 1" @click="page = Math.max(1, page - 1)">
            <v-icon small>chevron_left</v-icon>
          </button>
          <button
            type="button"
            class="refined-page-btn"
            :disabled="page >= totalPages"
            @click="page = Math.min(totalPages, page + 1)"
          >
            <v-icon small>chevron_right</v-icon>
          </button>
        </div>
      </div>
    </div>

    <v-dialog v-model="dialog" max-width="460px" persistent>
      <v-card>
        <v-card-title class="headline">Kâr Güncelle</v-card-title>
        <v-card-text>
          <p class="body-1 mb-2">
            Seçili ürün sayısı: <strong>{{ dialogRows.length }}</strong>
          </p>
          <p class="caption mb-3">
            Hedef <strong>Gerçekleşen Kâr (gram başına)</strong> değerini girin. Her ürün için MP fiyatının
            formül ve döviz ile nasıl hesaplandığı tersten çözülerek, bu kârı sağlayacak
            <strong>yeni döviz</strong> değeri hesaplanıp kaydedilir.
            Ürün çok ürünlü bir döviz grubundaysa o gruptan çıkarılıp ayrı bir satır oluşturulur.
          </p>
          <p class="caption mb-3 refined-muted">
            Kargo: <strong>{{ formatNum(kargoBedeli) }}</strong> ₺ · Hizmet:
            <strong>{{ formatNum(hizmetBedeli) }}</strong> ₺ · Komisyon:
            <strong>{{ komisyonPct != null ? formatNum(komisyonPct) : "0" }}</strong> % · Stopaj:
            <strong>{{ stopajPct != null ? formatNum(stopajPct) : "0" }}</strong> %
          </p>
          <v-text-field
            v-model="profitInput"
            label="Yeni Gerçekleşen Kâr (₺ / gr)"
            placeholder="Örn. 312,45"
            type="text"
            hide-details
            box
          ></v-text-field>
        </v-card-text>
        <v-card-actions>
          <v-spacer></v-spacer>
          <v-btn flat @click="dialog = false">İptal</v-btn>
          <v-btn color="primary" flat :loading="saving" @click="saveProfit">Kaydet</v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>

    <v-snackbar v-model="snackbar.show" :color="snackbar.color" :timeout="6000" top>
      {{ snackbar.text }}
      <v-btn flat @click="snackbar.show = false">Kapat</v-btn>
    </v-snackbar>
  </div>
</template>

<script>
import karHesabiApi from "@/api/karHesabi";
import goldPriceApi from "@/api/goldPrice";

export default {
  name: "KarHesabiIndex",
  data() {
    return {
      items: [],
      selected: [],
      categoryId: 0,
      kargoInput: "",
      hizmetInput: "",
      komisyonInput: "",
      stopajInput: "",
      goldPrice: null,
      goldPriceModifiedDate: null,
      calculated: false,
      loading: false,
      page: 1,
      rowsPerPage: 25,
      sortBy: "productName",
      sortDesc: false,
      dialog: false,
      dialogRows: [],
      profitInput: "",
      saving: false,
      snackbar: { show: false, text: "", color: "success" },
    };
  },
  computed: {
    categorySelectItems() {
      const all = this.$store.state.productCategory.all || [];
      return [{ id: 0, name: "Tümü" }].concat(
        all.map((c) => ({ id: c.id, name: c.name || `Kategori #${c.id}` }))
      );
    },
    kargoBedeli() {
      return this.parseNum(this.kargoInput);
    },
    hizmetBedeli() {
      return this.parseNum(this.hizmetInput);
    },
    komisyonPct() {
      return this.parseNum(this.komisyonInput);
    },
    stopajPct() {
      return this.parseNum(this.stopajInput);
    },
    totalCost() {
      if (this.kargoBedeli == null || this.hizmetBedeli == null) return null;
      return this.kargoBedeli + this.hizmetBedeli;
    },
    inputsReady() {
      return this.calculated && this.totalCost != null;
    },
    filteredItems() {
      let list = this.items;
      if (this.categoryId > 0) list = list.filter((i) => i.productCategoryId === this.categoryId);
      return list;
    },
    decoratedItems() {
      return this.filteredItems.map((i) => {
        const komisyonTl = this.computeKomisyon(i);
        const stopajTl = this.computeStopaj(i);
        const costTl = this.computeCostTl(i);
        const urunKari = this.computeUrunKari(i, komisyonTl, stopajTl, costTl);
        return {
          ...i,
          komisyonTl,
          stopajTl,
          costTl,
          urunKari,
          gerceklesenKar: this.computeGerceklesenKar(i, urunKari),
        };
      });
    },
    editableItems() {
      return this.decoratedItems.filter((i) => i.canCompute);
    },
    selectedEditable() {
      return (this.selected || []).filter((r) => r.canCompute);
    },
    sortedItems() {
      const numeric = new Set([
        "hasGram",
        "currentDoviz",
        "marketplacePrice",
        "costHas",
        "costTl",
        "komisyonTl",
        "stopajTl",
        "urunKari",
        "gerceklesenKar",
      ]);
      const list = [...this.decoratedItems];
      const key = this.sortBy;
      const desc = this.sortDesc;
      list.sort((a, b) => {
        let c = 0;
        if (numeric.has(key)) {
          const va = a[key] != null ? Number(a[key]) : null;
          const vb = b[key] != null ? Number(b[key]) : null;
          if (va == null && vb == null) c = 0;
          else if (va == null) return 1;
          else if (vb == null) return -1;
          else c = va < vb ? -1 : va > vb ? 1 : 0;
        } else {
          const va = (a[key] != null ? String(a[key]) : "").toLowerCase();
          const vb = (b[key] != null ? String(b[key]) : "").toLowerCase();
          c = va < vb ? -1 : va > vb ? 1 : 0;
        }
        return desc ? -c : c;
      });
      return list;
    },
    totalPages() {
      const n = this.sortedItems.length;
      if (n === 0) return 1;
      return Math.max(1, Math.ceil(n / this.rowsPerPage));
    },
    paginatedItems() {
      const start = (this.page - 1) * this.rowsPerPage;
      return this.sortedItems.slice(start, start + this.rowsPerPage);
    },
    paginationInfo() {
      const total = this.sortedItems.length;
      if (total === 0) return "0 kayıt";
      const start = (this.page - 1) * this.rowsPerPage + 1;
      const end = Math.min(this.page * this.rowsPerPage, total);
      return `${start}–${end} / ${total} kayıt`;
    },
    headerCheckboxAll() {
      const ed = this.editableItems;
      if (!ed.length) return false;
      return ed.every((i) => this.isRowSelected(i));
    },
    headerCheckboxIndeterminate() {
      const ed = this.editableItems;
      if (!ed.length) return false;
      const any = ed.some((i) => this.isRowSelected(i));
      return any && !this.headerCheckboxAll;
    },
  },
  watch: {
    page() {
      const max = this.totalPages;
      if (this.page > max) this.page = max;
    },
    totalPages(max) {
      if (this.page > max) this.page = max;
    },
  },
  async mounted() {
    await this.$store.dispatch("productCategory/getAllProductCategories");
    await this.loadGoldPrice();
    await this.load();
  },
  methods: {
    parseNum(s) {
      if (s == null) return null;
      let t = String(s).trim();
      if (!t) return null;
      const lastDot = t.lastIndexOf(".");
      const lastComma = t.lastIndexOf(",");
      if (lastDot > -1 && lastComma > -1) {
        // İki ayraç da var: en sağdaki ondalık ayraç kabul edilir.
        if (lastDot > lastComma) t = t.replace(/,/g, "");
        else t = t.replace(/\./g, "").replace(",", ".");
      } else {
        t = t.replace(",", ".");
      }
      const n = parseFloat(t);
      return Number.isNaN(n) ? null : n;
    },
    round2(x) {
      return Math.round((x + Number.EPSILON) * 100) / 100;
    },
    formatNum(v) {
      if (v == null) return "—";
      const n = Number(v);
      if (Number.isNaN(n)) return String(v);
      return n.toLocaleString("tr-TR", { minimumFractionDigits: 2, maximumFractionDigits: 2 });
    },
    formatGram(v) {
      if (v == null) return "—";
      const n = Number(v);
      if (Number.isNaN(n)) return String(v);
      return n.toLocaleString("tr-TR", { maximumFractionDigits: 4 });
    },
    trStr(n) {
      return String(n).replace(".", ",");
    },
    computeKomisyon(item) {
      if (this.komisyonPct == null || item.marketplacePrice == null) return null;
      return this.round2((Number(item.marketplacePrice) * this.komisyonPct) / 100);
    },
    computeStopaj(item) {
      if (this.stopajPct == null || item.marketplacePrice == null) return null;
      return this.round2((Number(item.marketplacePrice) * this.stopajPct) / 100);
    },
    computeCostTl(item) {
      // Maliyet (₺) = has maliyet (formülün 1., 2. ve 5. çarpanı) × güncel Has Altın fiyatı.
      if (this.goldPrice == null || item.costHas == null) return null;
      return this.round2(Number(item.costHas) * Number(this.goldPrice));
    },
    computeUrunKari(item, komisyonTl, stopajTl, costTl) {
      // Ürün Kârı (₺) = MP fiyatı − komisyon − stopaj − hizmet bedeli − kargo bedeli − maliyet (₺).
      if (!this.inputsReady) return null; // kargo/hizmet için «Karları Hesapla» gerekli
      if (item.marketplacePrice == null || komisyonTl == null || stopajTl == null || costTl == null)
        return null;
      return this.round2(
        Number(item.marketplacePrice) - komisyonTl - stopajTl - this.hizmetBedeli - this.kargoBedeli - costTl
      );
    },
    computeGerceklesenKar(item, urunKari) {
      // Gerçekleşen Kâr (Gr) = ürün kârı / maliyet (has).
      if (urunKari == null || item.costHas == null || Number(item.costHas) === 0) return null;
      return this.round2(urunKari / Number(item.costHas));
    },
    editTitle(item) {
      if (!item.canCompute) return "Has gram okunamadı";
      if (!this.inputsReady) return "Önce kargo/hizmet bedelini girip «Karları Hesapla»ya basın";
      if (this.selectedEditable.length > 1) return "Çoklu seçimde üstteki «Seçilenlerin Kârını Güncelle» kullanın";
      return "Düzenle";
    },
    onFilterChange() {
      this.page = 1;
    },
    calculate() {
      if (this.kargoBedeli == null || this.hizmetBedeli == null) {
        this.showSnack("Geçerli kargo ve hizmet bedeli girin.", "warning");
        return;
      }
      this.calculated = true;
    },
    async loadGoldPrice() {
      try {
        const res = await goldPriceApi.getGoldPrice();
        const data = res.data;
        if (data && data.price != null) {
          this.goldPrice = data.price;
          this.goldPriceModifiedDate = data.modifiedDate || null;
        }
      } catch (e) {
        // Has Altın fiyatı alınamazsa Maliyet (₺) kolonu boş kalır; sayfa yine de çalışır.
        this.goldPrice = null;
      }
    },
    async load() {
      this.loading = true;
      try {
        const res = await karHesabiApi.getProfitTable();
        this.items = res.data || [];
        this.selected = [];
        this.page = 1;
      } catch (e) {
        this.showSnack("Liste yüklenemedi.", "error");
      } finally {
        this.loading = false;
      }
    },
    isRowSelected(item) {
      return this.selected.some((s) => s.mpProductId === item.mpProductId);
    },
    toggleRowSelection(item) {
      if (!item.canCompute) return;
      const idx = this.selected.findIndex((s) => s.mpProductId === item.mpProductId);
      if (idx >= 0) this.selected.splice(idx, 1);
      else this.selected.push(item);
    },
    toggleAll() {
      const ed = this.editableItems;
      if (!ed.length) return;
      if (this.headerCheckboxAll) {
        const removeIds = new Set(ed.map((i) => i.mpProductId));
        this.selected = this.selected.filter((s) => !removeIds.has(s.mpProductId));
      } else {
        const map = new Map(this.selected.map((s) => [s.mpProductId, s]));
        ed.forEach((i) => map.set(i.mpProductId, i));
        this.selected = Array.from(map.values());
      }
    },
    onRowClick(item) {
      if (!item.canCompute) return;
      this.toggleRowSelection(item);
    },
    changeSort(column) {
      if (this.sortBy === column) this.sortDesc = !this.sortDesc;
      else {
        this.sortBy = column;
        this.sortDesc = false;
      }
    },
    openDialogForRow(item) {
      if (!item.canCompute) return;
      if (!this.inputsReady) {
        this.showSnack("Önce kargo/hizmet bedelini girip «Karları Hesapla»ya basın.", "warning");
        return;
      }
      this.selected = [item];
      this.dialogRows = [item];
      this.profitInput = item.gerceklesenKar != null ? this.trStr(item.gerceklesenKar) : "";
      this.dialog = true;
    },
    openDialogFromSelection() {
      const sel = this.selectedEditable;
      if (!sel.length) {
        this.showSnack("Güncellenebilir satır seçin (has gramı okunabilen).", "warning");
        return;
      }
      if (!this.inputsReady) {
        this.showSnack("Önce kargo/hizmet bedelini girip «Karları Hesapla»ya basın.", "warning");
        return;
      }
      // Güncel kâr değerleri için satırları taze decoratedItems'tan yeniden kur (eski referanslar bayat olabilir).
      const byId = new Set(sel.map((s) => s.mpProductId));
      const rows = this.decoratedItems.filter((d) => byId.has(d.mpProductId));
      this.dialogRows = rows;
      if (rows.length === 1 && rows[0].gerceklesenKar != null)
        this.profitInput = this.trStr(rows[0].gerceklesenKar);
      else this.profitInput = "";
      this.dialog = true;
    },
    async saveProfit() {
      const profit = this.parseNum(this.profitInput);
      if (profit == null) {
        this.showSnack("Geçerli bir kâr değeri girin.", "warning");
        return;
      }
      if (!this.inputsReady) {
        this.showSnack("Kargo ve hizmet bedeli geçerli olmalı.", "warning");
        return;
      }
      this.saving = true;
      try {
        const body = {
          mpProductIds: this.dialogRows.map((r) => r.mpProductId),
          profit,
          kargoBedeli: this.kargoBedeli,
          hizmetBedeli: this.hizmetBedeli,
          komisyonPct: this.komisyonPct || 0,
          stopajPct: this.stopajPct || 0,
        };
        const res = await karHesabiApi.updateDoviz(body);
        const results = (res.data && res.data.results) || [];
        const failed = results.filter((r) => !r.success);
        if (failed.length) {
          const msg = failed.map((f) => `#${f.mpProductId}: ${f.message || "Hata"}`).join("; ");
          this.showSnack(`Bazı satırlar güncellenemedi. ${msg}`, "error");
        } else {
          this.showSnack(`Döviz güncellendi (${results.length} ürün).`, "success");
        }
        this.dialog = false;
        await this.load();
      } catch (e) {
        const msg =
          (e.response && e.response.data && e.response.data.message) || e.message || "Kayıt başarısız.";
        this.showSnack(msg, "error");
      } finally {
        this.saving = false;
      }
    },
    showSnack(text, color) {
      this.snackbar = { show: true, text, color: color || "success" };
    },
  },
};
</script>

<style scoped>
.partner-tab-section {
  max-width: 110rem;
  margin: 0 auto;
  padding: 2rem 1.5rem;
  font-family: "Inter", -apple-system, BlinkMacSystemFont, sans-serif;
}

.refined-header {
  display: flex;
  flex-wrap: wrap;
  align-items: center;
  justify-content: space-between;
  gap: 1rem;
  margin-bottom: 1.5rem;
}

.refined-title {
  font-size: 1.5rem;
  font-weight: 700;
  color: #0f172a;
  margin: 0;
  letter-spacing: -0.025em;
}

.refined-subtitle {
  font-size: 0.875rem;
  color: #64748b;
  margin: 0.25rem 0 0 0;
}

.refined-primary-btn {
  display: inline-flex;
  align-items: center;
  background: #2563eb;
  color: #fff;
  padding: 0.625rem 1.5rem;
  border-radius: 0.5rem;
  font-weight: 600;
  font-size: 0.875rem;
  border: none;
  cursor: pointer;
  text-decoration: none;
  box-shadow: 0 1px 2px rgba(37, 99, 235, 0.2);
  transition: background 0.2s, box-shadow 0.2s;
}

.refined-primary-btn:hover:not(:disabled) {
  background: #1d4ed8;
}

.refined-primary-btn:disabled {
  opacity: 0.7;
  cursor: not-allowed;
}

.refined-btn-success {
  background: #059669;
  box-shadow: 0 1px 2px rgba(5, 150, 105, 0.2);
}

.refined-btn-success:hover:not(:disabled) {
  background: #047857;
}

.refined-btn-success-emphasis {
  box-shadow: 0 0 0 3px rgba(5, 150, 105, 0.35), 0 4px 14px rgba(5, 150, 105, 0.35) !important;
  transform: scale(1.02);
}

.refined-header-actions {
  display: flex;
  align-items: center;
  gap: 0.75rem;
  flex-wrap: wrap;
}

.refined-filters {
  display: flex;
  flex-wrap: wrap;
  gap: 1rem;
  margin-bottom: 1.5rem;
  align-items: flex-end;
}

.refined-filter-item {
  display: flex;
  flex-direction: column;
  gap: 0.25rem;
}

.refined-filter-submit .refined-primary-btn {
  margin-top: 1.25rem;
}

.refined-filter-label {
  font-size: 0.75rem;
  font-weight: 600;
  color: #64748b;
  text-transform: uppercase;
  letter-spacing: 0.05em;
}

.refined-input,
.refined-select {
  padding: 0.5rem 0.75rem;
  border-radius: 0.5rem;
  border: 1px solid #e2e8f0;
  font-size: 0.875rem;
  color: #0f172a;
  min-width: 180px;
  background: #fff;
}

.refined-input-narrow {
  min-width: 130px;
}

.refined-input:focus,
.refined-select:focus {
  outline: none;
  border-color: #2563eb;
  box-shadow: 0 0 0 2px rgba(37, 99, 235, 0.15);
}

.refined-table-wrap {
  background: #fff;
  border-radius: 0.625rem;
  box-shadow: 0 1px 3px rgba(0, 0, 0, 0.08);
  border: 1px solid #e2e8f0;
  overflow: hidden;
}

.refined-table-scroll {
  overflow-x: auto;
}

.refined-table {
  width: 100%;
  text-align: left;
  border-collapse: collapse;
}

.refined-table thead tr {
  background: #f8fafc;
  border-bottom: 1px solid #e2e8f0;
}

.refined-table th {
  padding: 1rem 1.5rem;
  font-size: 0.75rem;
  font-weight: 600;
  color: #64748b;
  text-transform: uppercase;
  letter-spacing: 0.05em;
  vertical-align: middle;
}

.refined-th-check {
  width: 52px;
  text-align: center;
  padding-left: 1rem !important;
  padding-right: 0.5rem !important;
}

.refined-th-sort {
  cursor: pointer;
  user-select: none;
}

.refined-th-sort:hover {
  color: #2563eb;
}

.refined-sort-icon {
  vertical-align: middle;
  margin-left: 2px;
}

.refined-th-num {
  text-align: right;
}

.refined-th-actions {
  text-align: center;
  width: 120px;
}

.refined-table tbody tr {
  border-bottom: 1px solid #f1f5f9;
  transition: background 0.15s;
  cursor: default;
}

.refined-table tbody tr:hover {
  background: rgba(248, 250, 252, 0.7);
}

.cost-row-selected {
  background: rgba(37, 99, 235, 0.2) !important;
  box-shadow: inset 4px 0 0 0 #1d4ed8;
}

.cost-row-selected:hover {
  background: rgba(37, 99, 235, 0.28) !important;
}

.refined-table td {
  padding: 1rem 1.5rem;
  font-size: 0.875rem;
  color: #475569;
  vertical-align: middle;
}

.refined-td-check {
  width: 52px;
  text-align: center;
  padding-left: 1rem !important;
  padding-right: 0.5rem !important;
}

.refined-td-actions {
  text-align: center;
}

.refined-cell-bold {
  font-weight: 600;
  color: #1e293b;
}

.refined-cell-value {
  font-weight: 500;
  color: #1e293b;
}

.refined-kar {
  color: #047857;
  font-weight: 700;
}

.refined-muted {
  color: #94a3b8;
}

.refined-action-btn {
  padding: 0.35rem 0.75rem;
  border-radius: 0.375rem;
  border: none;
  background: #2563eb;
  color: #fff;
  font-size: 0.8125rem;
  font-weight: 600;
  cursor: pointer;
  transition: background 0.2s;
}

.refined-action-btn:hover:not(:disabled) {
  background: #1d4ed8;
}

.refined-action-btn:disabled {
  opacity: 0.45;
  cursor: not-allowed;
}

.refined-action-btn.refined-action-btn-muted:disabled {
  background: #cbd5e1 !important;
  color: #64748b !important;
  box-shadow: none !important;
  opacity: 0.85;
  cursor: not-allowed;
  pointer-events: none;
}

.refined-loading,
.refined-empty {
  text-align: center;
  padding: 2rem !important;
  color: #64748b;
}

.refined-table-footer {
  display: flex;
  align-items: center;
  justify-content: flex-end;
  gap: 1.5rem;
  padding: 1rem 1.5rem;
  background: rgba(248, 250, 252, 0.6);
  font-size: 0.875rem;
  color: #64748b;
  flex-wrap: wrap;
}

.refined-footer-left {
  display: flex;
  align-items: center;
  gap: 0.75rem;
}

.refined-rows-select {
  background: transparent;
  border: none;
  color: #0f172a;
  cursor: pointer;
  font-weight: 500;
  padding: 0.25rem 0;
}

.refined-rows-select:focus {
  outline: none;
}

.refined-pagination-info {
  margin-left: auto;
}

.refined-footer-actions {
  display: flex;
  align-items: center;
  gap: 0.25rem;
}

.refined-page-btn {
  padding: 0.25rem;
  border: none;
  background: transparent;
  border-radius: 0.25rem;
  cursor: pointer;
  color: #64748b;
  display: inline-flex;
  align-items: center;
  justify-content: center;
}

.refined-page-btn:hover:not(:disabled) {
  background: #e2e8f0;
  color: #0f172a;
}

.refined-page-btn:disabled {
  opacity: 0.35;
  cursor: not-allowed;
}

.cost-refined-table >>> .v-input--selection-controls {
  margin-top: 0 !important;
  padding-top: 0 !important;
}

/* Kolon sayısı fazla; sayfaya sığması için yatay boşlukları daralt. */
.cost-refined-table th,
.cost-refined-table td {
  padding-left: 0.75rem;
  padding-right: 0.75rem;
  white-space: nowrap;
}

/* Ürün adı uzun olabilir; bu kolon sarsın, sayısal kolonlar tek satırda kalsın. */
.cost-refined-table td.refined-cell-bold {
  white-space: normal;
  min-width: 14rem;
}
</style>
